import ApiChatbot from "@/api/methods/ApiChatbot";
import ApiNotes from "@/api/methods/ApiNotes";
import ApiUserProfile from "@/api/methods/ApiUserProfile";

function quotaFromBody(data) {
    if (!data) {
        return null;
    }
    if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (_) { return null; }
    }
    if (data.error === "OUT_OF_QUOTA") {
        return { privilegeCode: data.privilege_code, upgradeUrl: data.upgrade_url || "/personal" };
    }
    if (typeof data.detail === "string") {
        try {
            const parsed = JSON.parse(data.detail);
            if (parsed && parsed.error === "OUT_OF_QUOTA") {
                return { privilegeCode: parsed.privilege_code, upgradeUrl: parsed.upgrade_url || "/personal" };
            }
        } catch (_) { /* not JSON */ }
    }
    if (data.detail && typeof data.detail === "object" && data.detail.error === "OUT_OF_QUOTA") {
        return { privilegeCode: data.detail.privilege_code, upgradeUrl: data.detail.upgrade_url || "/personal" };
    }
    return null;
}

function extractQuotaError(e) {
    if (e && e.response && e.response.status === 402) {
        const fromBody = quotaFromBody(e.response.data);
        if (fromBody) {
            return fromBody;
        }
        return { privilegeCode: null, upgradeUrl: "/personal" };
    }
    if (!e || !e.response) {
        return null;
    }
    return quotaFromBody(e.response.data);
}

function extractErrorMessage(e) {
    if (!e.response) {
        return e.message;
    }
    let detail = e.response.data && e.response.data.detail;
    if (typeof detail === "string") {
        try {
            const parsed = JSON.parse(detail);
            if (parsed && parsed.detail) {
                return parsed.detail;
            }
        } catch (_) { /* not JSON, use as-is */ }
    }
    return detail || e.message;
}

const MESSAGES_LIMIT = 20;

const CHATBOT_LIMIT_CODES = [
    "CHATBOT_PLATFORM_QUESTION",
    "CHATBOT_NOTE_INSIGHT",
    "CHATBOT_DOMAIN_QUESTION",
];

export default {
    namespaced: true,
    state: {
        sessions: [],
        currentSession: null,
        messages: [],
        isTyping: false,
        notesByFilter: { MY: [], SHARED: [], MARKETPLACE: [], BUYER_OFFERS: [], PRESELECTED: [] },
        selectedNoteIds: [],
        error: null,
        questionType: "PLATFORM",
        chatbotLimits: {},
        hints: {},
        messagesOffset: 0,
        messagesHasMore: false,
        isLoadingMore: false,
    },
    getters: {
        allNotes(state) {
            const seen = new Set();
            return Object.values(state.notesByFilter).flat().filter(n => {
                const id = Number(n.noteId);
                if (seen.has(id)) {
                    return false;
                }
                seen.add(id);
                return true;
            });
        },
        currentSessionNotes(state, getters) {
            if (!state.currentSession || !state.currentSession.note_ids) {
                return [];
            }
            return getters.allNotes.filter(n => state.currentSession.note_ids.includes(n.noteId));
        },
        selectedNotes(state, getters) {
            return getters.allNotes.filter(n => state.selectedNoteIds.some(id => Number(id) === Number(n.noteId)));
        },
    },
    actions: {
        async fetchSessions({ commit }) {
            try {
                const res = await ApiChatbot.getUserSessions();
                commit("setSessions", res.data);
            } catch (e) {
                commit("setError", "Could not load sessions");
            }
        },

        async fetchMessages({ commit }, sessionId) {
            try {
                const res = await ApiChatbot.getMessages(sessionId, MESSAGES_LIMIT, 0);
                const messages = res.data.map(m => ({ ...m, questionType: m.question_type || undefined }));
                commit("setMessages", messages);
                commit("setMessagesOffset", MESSAGES_LIMIT);
                commit("setMessagesHasMore", res.data.length >= MESSAGES_LIMIT);
            } catch (e) {
                commit("setError", "Could not load messages");
            }
        },

        async fetchOlderMessages({ commit, state }) {
            if (!state.messagesHasMore || state.isLoadingMore || !state.currentSession) { return; }
            commit("setIsLoadingMore", true);
            try {
                const res = await ApiChatbot.getMessages(state.currentSession.session_id, MESSAGES_LIMIT, state.messagesOffset);
                const messages = res.data.map(m => ({ ...m, questionType: m.question_type || undefined }));
                commit("prependMessages", messages);
                commit("setMessagesOffset", state.messagesOffset + MESSAGES_LIMIT);
                commit("setMessagesHasMore", res.data.length >= MESSAGES_LIMIT);
            } catch (e) {
                commit("setError", "Could not load messages");
            } finally {
                commit("setIsLoadingMore", false);
            }
        },

        async switchSession({ commit, dispatch }, session) {
            commit("setIsTyping", false);
            commit("setCurrentSession", session);
            commit("setMessages", []);
            commit("setMessagesOffset", 0);
            commit("setMessagesHasMore", false);
            commit("setIsLoadingMore", false);
            await dispatch("fetchMessages", session.session_id);
        },

        async createSessionAndSend({ commit, state, dispatch }, payload) {
            const { message, noteIdsOverride, questionTypeOverride, retryOfId } = payload;
            commit("setIsTyping", true);
            const questionType = questionTypeOverride || state.questionType;
            const noteIds = noteIdsOverride
                ? [...noteIdsOverride]
                : (questionType === "NOTE_INSIGHT" ? [...state.selectedNoteIds] : []);
            const now = new Date().toISOString();
            const tmpId = retryOfId || Date.now();
            if (retryOfId) {
                commit("clearMessageFailed", retryOfId);
            } else {
                commit("addMessage", { message_id: tmpId, sender_type: "user", content: message, created_at: now, note_ids: noteIds, questionType });
            }
            try {
                const res = await ApiChatbot.createSession(message, noteIds, questionType);
                const successQuota = quotaFromBody(res.data);
                if (successQuota) {
                    commit("markMessageQuotaFailed", { id: tmpId, quota: successQuota });
                    dispatch("fetchChatbotLimits");
                    return;
                }
                const session = res.data.session_data;
                const msgData = res.data.message_data;
                commit("addSession", session);
                commit("setCurrentSession", session);
                commit("updateMessageId", { oldId: tmpId, newId: msgData.req_message_id });
                commit("addMessage", { message_id: msgData.res_message_id, sender_type: "bot", content: msgData.answer, created_at: now });
                dispatch("fetchChatbotLimits");
            } catch (e) {
                console.error("[Chatbot] createSession failed", e && e.response && { status: e.response.status, data: e.response.data }, e);
                const quota = extractQuotaError(e);
                if (quota) {
                    commit("markMessageQuotaFailed", { id: tmpId, quota });
                    dispatch("fetchChatbotLimits");
                } else {
                    commit("markMessageFailed", tmpId);
                    commit("setError", extractErrorMessage(e));
                }
            } finally {
                commit("setIsTyping", false);
            }
        },

        async sendMessage({ commit, state, dispatch }, payload) {
            const { message, noteIdsOverride, questionTypeOverride, retryOfId } = payload;
            if (!state.currentSession) {
                return;
            }
            const sessionId = state.currentSession.session_id;
            commit("setIsTyping", true);
            const questionType = questionTypeOverride || state.questionType;
            const noteIds = noteIdsOverride
                ? [...noteIdsOverride]
                : (questionType === "NOTE_INSIGHT" ? [...state.selectedNoteIds] : []);
            const now = new Date().toISOString();
            const tmpId = retryOfId || Date.now();
            if (retryOfId) {
                commit("clearMessageFailed", retryOfId);
            } else {
                commit("addMessage", { message_id: tmpId, sender_type: "user", content: message, created_at: now, note_ids: noteIds, questionType });
            }
            try {
                const res = await ApiChatbot.sendMessage(sessionId, message, noteIds, questionType);
                if (!state.currentSession || state.currentSession.session_id !== sessionId) {
                    return;
                }
                const successQuota = quotaFromBody(res.data);
                if (successQuota) {
                    commit("markMessageQuotaFailed", { id: tmpId, quota: successQuota });
                    dispatch("fetchChatbotLimits");
                    return;
                }
                commit("updateMessageId", { oldId: tmpId, newId: res.data.req_message_id });
                commit("addMessage", {
                    message_id: res.data.res_message_id,
                    sender_type: "bot",
                    content: res.data.answer,
                    created_at: now,
                });
                dispatch("fetchChatbotLimits");
            } catch (e) {
                console.error("[Chatbot] sendMessage failed", e && e.response && { status: e.response.status, data: e.response.data }, e);
                const quota = extractQuotaError(e);
                if (quota) {
                    commit("markMessageQuotaFailed", { id: tmpId, quota });
                    dispatch("fetchChatbotLimits");
                } else {
                    commit("markMessageFailed", tmpId);
                    commit("setError", extractErrorMessage(e));
                }
            } finally {
                commit("setIsTyping", false);
            }
        },

        async retryMessage({ state, dispatch }, messageId) {
            const msg = state.messages.find(m => m.message_id === messageId);
            if (!msg) {
                return;
            }
            const payload = {
                message: msg.content,
                noteIdsOverride: msg.note_ids || [],
                questionTypeOverride: msg.questionType,
                retryOfId: messageId,
            };
            if (state.currentSession) {
                await dispatch("sendMessage", payload);
            } else {
                await dispatch("createSessionAndSend", payload);
            }
        },

        removeFailedMessage({ commit }, messageId) {
            commit("removeMessage", messageId);
        },

        async renameSession({ commit }, { sessionId, name }) {
            try {
                await ApiChatbot.updateSession(sessionId, name);
                commit("updateSessionName", { sessionId, name });
            } catch (e) {
                commit("setError", "Rename failed");
            }
        },

        async deleteSession({ commit, state }, sessionId) {
            try {
                await ApiChatbot.deleteSession(sessionId);
                commit("removeSession", sessionId);
                if (state.currentSession && state.currentSession.session_id === sessionId) {
                    commit("setCurrentSession", null);
                    commit("setMessages", []);
                }
            } catch (e) {
                commit("setError", "Delete failed");
            }
        },

        async fetchNotes({ commit, state }, payload) {
            const noteType = typeof payload === "string" ? payload : (payload && payload.noteType) || "MY";
            const explicitNoteIds = payload && typeof payload === "object" && Array.isArray(payload.noteIds) && payload.noteIds.length > 0;
            const noteIds = explicitNoteIds ? payload.noteIds : state.selectedNoteIds;

            if (!explicitNoteIds && state.notesByFilter[noteType] && state.notesByFilter[noteType].length > 0) {
                return;
            }
            try {
                const res = await ApiNotes.getNotesForChatbot(noteType, noteIds);
                const notes = Array.isArray(res.data.notes) ? res.data.notes : [];
                commit("setNotesForFilter", { noteType, notes });

                if (explicitNoteIds) {
                    const selectedNotes = Array.isArray(res.data.selectedNotes) ? res.data.selectedNotes : [];
                    const selectedIds = selectedNotes.map(n => Number(n.noteId));
                    if (selectedIds.length > 0) {
                        commit("setSelectedNoteIds", selectedIds);
                        const tabNoteIds = new Set(notes.map(n => Number(n.noteId)));
                        const notesNotInTab = selectedNotes.filter(n => !tabNoteIds.has(Number(n.noteId)));
                        if (notesNotInTab.length > 0) {
                            const existing = state.notesByFilter.PRESELECTED || [];
                            const existingIds = new Set(existing.map(n => Number(n.noteId)));
                            const toAdd = notesNotInTab.filter(n => !existingIds.has(Number(n.noteId)));
                            if (toAdd.length > 0) {
                                commit("setNotesForFilter", { noteType: "PRESELECTED", notes: [...existing, ...toAdd] });
                            }
                        }
                    }
                }
            } catch (e) {
                commit("setError", "Could not load notes");
            }
        },

        async fetchChatbotLimits({ commit, rootState }) {
            try {
                const token = rootState.UserInfo.token;
                const res = await ApiUserProfile.getSubscriptionLimits(token);
                const all = res.data && res.data.data ? res.data.data : [];
                const limitsMap = {};
                const NAME_TO_CODE = {
                    "AI Platform Help": "CHATBOT_PLATFORM_QUESTION",
                    "AI Business Questions": "CHATBOT_DOMAIN_QUESTION",
                    "AI Note Analysis": "CHATBOT_NOTE_INSIGHT",
                };
                CHATBOT_LIMIT_CODES.forEach(code => {
                    const apiName = Object.keys(NAME_TO_CODE).find(name => NAME_TO_CODE[name] === code);
                    const info = all.find(item => item.Name === apiName);
                    if (info) {
                        const subscription = Number(info.Subscription) || 0;
                        const addon = Number(info['Add-on']) || 0;
                        const used = Number(info.Used) || 0;
                        const available = Number(info.Available) || 0;
                        const limit = subscription + addon;
                        const unlimited = available >= 99999;
                        limitsMap[code] = { used, limit, available, unlimited };
                    } else {
                        limitsMap[code] = { used: 0, limit: 0, available: null, unlimited: false };
                    }
                });
                commit("setChatbotLimits", limitsMap);
            } catch (e) {
                // non-fatal — quota rings just stay in default state
            }
        },

        async fetchHints({ commit }) {
            try {
                const res = await ApiChatbot.getHints();
                const hintsByType = {};
                (res.data || []).forEach(hint => {
                    if (!hint.questionType || !hint.questionText) {
                        return;
                    }
                    if (!hintsByType[hint.questionType]) {
                        hintsByType[hint.questionType] = [];
                    }
                    hintsByType[hint.questionType].push(hint.questionText);
                });
                commit("setHints", hintsByType);
            } catch (e) {
                // non-fatal
            }
        },

        newChat({ commit }) {
            commit("setIsTyping", false);
            commit("setCurrentSession", null);
            commit("setMessages", []);
            commit("setSelectedNoteIds", []);
            commit("setMessagesOffset", 0);
            commit("setMessagesHasMore", false);
            commit("setIsLoadingMore", false);
        },

        toggleNoteSelection({ commit, state }, noteId) {
            const normalizedId = Number(noteId);
            const ids = state.selectedNoteIds.map(Number);
            const idx = ids.indexOf(normalizedId);
            if (idx > -1) {
                ids.splice(idx, 1);
            } else {
                ids.push(normalizedId);
            }
            commit("setSelectedNoteIds", ids);
        },

        setQuestionType({ commit }, type) {
            commit("setQuestionType", type);
        },

        clearError({ commit }) {
            commit("setError", null);
        },
    },
    mutations: {
        setSessions(state, sessions) {
            state.sessions = sessions;
            },
        setCurrentSession(state, session) {
            state.currentSession = session;
            },
        setMessages(state, messages) {
            state.messages = messages;
            },
        setIsTyping(state, val) {
            state.isTyping = val;
            },
        setNotesForFilter(state, { noteType, notes }) {
            state.notesByFilter = { ...state.notesByFilter, [noteType]: notes };
        },
        setSelectedNoteIds(state, ids) {
            state.selectedNoteIds = ids;
            },
        setError(state, err) {
            state.error = err;
            },
        setQuestionType(state, type) {
            state.questionType = type;
            },
        setChatbotLimits(state, limits) {
            state.chatbotLimits = limits;
            },
        setHints(state, hints) {
            state.hints = hints;
            },
        prependMessages(state, messages) {
            state.messages = [...messages, ...state.messages];
            },
        setMessagesOffset(state, offset) {
            state.messagesOffset = offset;
            },
        setMessagesHasMore(state, val) {
            state.messagesHasMore = val;
            },
        setIsLoadingMore(state, val) {
            state.isLoadingMore = val;
            },
        addSession(state, session) {
            state.sessions.unshift(session);
            },
        removeSession(state, id) {
            state.sessions = state.sessions.filter(s => s.session_id !== id);
            },
        updateSessionName(state, { sessionId, name }) {
            const s = state.sessions.find(s => s.session_id === sessionId);
            if (s) {
                s.name = name;
            }
            if (state.currentSession && state.currentSession.session_id === sessionId) {
                state.currentSession = { ...state.currentSession, name };
            }
        },
        addMessage(state, msg) {
            state.messages.push(msg);
            },
        removeMessage(state, id) {
            state.messages = state.messages.filter(m => m.message_id !== id);
            },
        updateMessageId(state, { oldId, newId }) {
            const msg = state.messages.find(m => m.message_id === oldId);
            if (msg) {
                msg.message_id = newId;
                msg.failed = false;
                msg.quotaError = null;
            }
        },
        markMessageFailed(state, id) {
            const msg = state.messages.find(m => m.message_id === id);
            if (msg) {
                state.messages = state.messages.map(m => m.message_id === id ? { ...m, failed: true, quotaError: null } : m);
            }
        },
        markMessageQuotaFailed(state, { id, quota }) {
            state.messages = state.messages.map(m => m.message_id === id ? { ...m, failed: true, quotaError: quota } : m);
        },
        clearMessageFailed(state, id) {
            state.messages = state.messages.map(m => m.message_id === id ? { ...m, failed: false, quotaError: null } : m);
        },
    },
};
